import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnInit,
} from '@angular/core';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { LoyaltyProgramHelper } from '@rd/helpers/loyalty-program.helper';
import { IconOptions, LoyaltyProgram } from '@rd/types';
import {
  LoyaltyProgramNameAttributes,
  TransferConnectV2SettingService,
} from '../../services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';

type ComponentRenderState = 'loading' | 'content';

@Component({
  selector: 'rd-tcv2-checkout-terms-list',
  templateUrl: './checkout-terms-list.component.html',
  styleUrls: ['./checkout-terms-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TcV2CheckoutTermsListComponent implements OnInit {
  @HostBinding('class.rd-tcv2-checkout-terms-list') componentClass = true;
  @HostBinding('class.rounded-md-desktop') desktopRadius = true;
  @HostBinding('class.rounded-md-t-mobile') mobileTopRadius = true;
  @Input() loading: boolean;
  @Input() loyaltyProgram: LoyaltyProgram;

  flippableIconOptions: IconOptions = {
    flippable: true,
  };

  constructor(
    protected iconRegistryService: IconRegistryService,
    protected loyaltyCurrencyService: LoyaltyCurrencyService,
    protected loyaltyProgramHelper: LoyaltyProgramHelper,
    protected numberService: NumberService,
    protected tcv2SettingService: TransferConnectV2SettingService,
  ) {}

  ngOnInit(): void {
    this.registerIcons();
  }

  getComponentRenderState(): ComponentRenderState {
    if (this.loading) {
      return 'loading';
    } else {
      return 'content';
    }
  }

  sourceAmountTranslationParams(): { points_amount: string; currency: string } {
    const nameAttr: LoyaltyProgramNameAttributes =
      this.tcv2SettingService
        .checkoutTransferAmountFormSourceCurrencyNameAttribute;
    const currency: string = this.loyaltyCurrencyService.loyaltyCurrencySelected
      ? this.loyaltyCurrencyService.loyaltyCurrencySelected[nameAttr]
      : '';

    return {
      points_amount: this.numberService.formatNumber(
        this.loyaltyProgram.transferRate?.denomination || 0,
      ),
      currency,
    };
  }

  destinationAmountTranslationParams(): {
    points_amount: string;
    currency: string;
  } {
    return {
      points_amount: this.numberService.formatNumber(
        this.loyaltyProgram.transferRate?.targetDenomination || 0,
      ),
      currency: this.loyaltyProgram.loyaltyCurrency?.name || '',
    };
  }

  estimatedTransferTimeParams(): { duration: string } {
    return {
      duration: this.loyaltyProgramHelper.transferTime(
        this.loyaltyProgram.pointsTransferCapability.processingDelay,
      ),
    };
  }

  minimumPointsTransferTranslationParams(): {
    points_amount: string;
    currency: string;
  } {
    return {
      points_amount: this.numberService.formatNumber(
        this.loyaltyProgram.transferRate?.minimumTransfer || 0,
      ),
      currency:
        this.loyaltyCurrencyService.loyaltyCurrencySelected?.shortName || '',
    };
  }

  private registerIcons(): void {
    this.iconRegistryService.registerIcon('convert-points');
    this.iconRegistryService.registerIcon('minimum-transfer');
    this.iconRegistryService.registerIcon('clock');
  }
}
